import { useMemo } from 'react';
import { getWeekDay, getDateParts, constructDate, getArrayOfDates, getDayOfWeekNames } from '../../components/Calendar/helpers';
import { getAllEvents, getWorkingHours } from '../../components/Calendar/apiService';
import { useQuery } from '@tanstack/react-query';
import { dateStart } from '../../utils/util';

const useDateEvents = ({start_date, end_date, range_type = "day", stylists}) => {
  // 1. Stabilize the primitives to prevent unnecessary effect triggers
  const { day, month, year } = getDateParts(start_date) || {};
  const stylistsStr = stylists?.join(",");


  // 2. Memoize the date objects
  // This ensures 'start' and 'end' references only change if the date values change
  const { start, end, dates, dayNames } = useMemo(() => {
    const s = constructDate(day, month, year);
    const e = end_date ? end_date : s;
    const d = getArrayOfDates(s, e);
    const names = range_type !== "month" ? getDayOfWeekNames(s, e) : getDayOfWeekNames();
    
    return { start: s, end: e, dates: d, dayNames: names };
  }, [day, month, year, end_date, range_type]);

  // console.log(start_date, day, start, "start_date!!!!!!!!!!!!!!!1");

  // 3. Use stable primitives in the Query Key
  // Instead of passing the whole 'start_date' object, use the ISO string or day/month/year
  const { data, isLoading: eventsLoading } = useQuery({
    queryKey: ["appointments", "calendar", start, end, stylistsStr], 
    queryFn: () => getAllEvents(start, end, stylistsStr),
    enabled: !!day && !!year,
  });

  const { data: workingHours } = useQuery({
    queryKey: ["workingHours", dayNames, stylistsStr], 
    queryFn: () => getWorkingHours(dayNames, stylistsStr),
    enabled: !!dayNames?.length
  });

  // 4. Memoize helper functions to prevent child components from re-rendering
  const filterByStylist = useMemo(() => (stylist_id, events = []) => {
    return events.filter(event => event.stylist_id?.toString() === stylist_id?.toString());
  }, []);

  const filterByDay = useMemo(() => (date, events = [], key = "appointment_date") => {
    return events.filter(event => dateStart(event[key]) === dateStart(date));
  }, []);

  const filterWorkingHours = useMemo(() => (workHours = [], stylist, dayName) => {
    if (!dayName) return;
    return workHours.find(wh => 
      (wh.stylist_id?.toString() === stylist?.toString() || wh.is_public) && 
      wh.day_of_week === dayName.toLowerCase()
    );
  }, []);

  return {
    day,
    month,
    year,
    dayOfWeek: getWeekDay(start),
    fullDate: start,
    dates,
    scheduledEvents: data?.events || [],
    workingHours,
    filterByStylist,
    filterByDay,
    filterWorkingHours,
    isLoading: eventsLoading
  };
}

export default useDateEvents;